import { useApolloClient } from '@apollo/client';
import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import React, { Dispatch, SetStateAction } from 'react';
import VoxelAlert from '../../../components/voxel/Alert/VoxelAlert';
import VoxelDialog from '../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelInput from '../../../components/voxel/Forms/VoxelInput';
import VoxelSelect, { GenericSelectOption } from '../../../components/voxel/Forms/VoxelSelect';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { PublicUserGq } from '../../../graphql/users/models/PublicUserGq';
import {
  UserTeamRoleGq,
  userCanEditMemberWithRole,
} from '../../../graphql/users/models/UserTeamRoleGq';
import {
  GetUserExistsResponseGq,
  GetUserExistsVariablesGq,
  getUserExistsGqTag,
} from '../../../graphql/users/queries/useGetUserExistGq';
import { usersApiService } from '../../../graphql/users/usersApiService';
import LicenseIncreaseWarningDialog from '../SubscriptionPage/LicenseIncreaseWarningDialog';
import { useLicenseIncreaseWarning } from '../SubscriptionPage/useLicenseIncreaseWarning';

export interface InviteMemberDialogProps {
  onSuccess: (user: PublicUserGq) => Promise<unknown>;
  user: CurrentUserGq;
  setShowSuccessDialog: Dispatch<SetStateAction<string>>;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

interface FormState {
  email: string;
  role: UserTeamRoleGq;
  validDomain?: boolean;
  ignoreDomain?: boolean;
  userAlreadyExist?: boolean;
}

const InviteMemberDialog: React.FC<InviteMemberDialogProps> = ({
  onSuccess,
  user,
  setShowSuccessDialog,
  open,
  setOpen,
}) => {
  const inviteMutation = usersApiService.mutations.useInviteUser();
  const apolloClient = useApolloClient();

  // Only allow these options for higher roles (so that you cannot invite over your own role)
  var memberRoleList = [
    {
      value: UserTeamRoleGq.MEMBER,
      title: 'Member',
      subtitle: 'Basic access',
    },
  ];

  if (userCanEditMemberWithRole(user, UserTeamRoleGq.ADMIN)) {
    memberRoleList.push({
      value: UserTeamRoleGq.ADMIN,
      title: 'Admin',
      subtitle: 'May edit basic settings, invite new users and set up team connections',
    });
  }
  if (userCanEditMemberWithRole(user, UserTeamRoleGq.OWNER)) {
    memberRoleList.push({
      value: UserTeamRoleGq.OWNER,
      title: 'Owner',
      subtitle: 'May edit subscription and billing in addition to admin rights',
    });
  }

  const licenseWarningBag = useLicenseIncreaseWarning(user.team);
  const formikProps = useFormik<FormState>({
    initialValues: {
      email: '',
      role: UserTeamRoleGq.MEMBER,
    },
    validate: (values) => {
      if (!values.email.length || !values.email.includes('@')) {
        return {
          email: 'Add a valid email',
        };
      }
      return {};
    },
    onSubmit: async (previousValues, actions) => {
      // Check email and domain if relevant
      if (
        previousValues.validDomain === undefined ||
        previousValues.userAlreadyExist === undefined
      ) {
        const userExistsQuery = await apolloClient.query<
          GetUserExistsResponseGq,
          GetUserExistsVariablesGq
        >({
          query: getUserExistsGqTag,
          variables: {
            command: {
              email: previousValues.email,
            },
          },
        });

        const nextValues = { ...previousValues };

        const userExists = userExistsQuery.data.users.getUserExist.alreadyInvited;

        // Email already exist in db
        if (!userExistsQuery.loading && userExists) {
          nextValues.userAlreadyExist = true;
        }

        nextValues.validDomain = await validateGoogleMailDomain(previousValues.email);

        // Domain not valid
        if (previousValues.validDomain !== undefined) {
          nextValues.ignoreDomain = true;
        }

        // Update values
        actions.setValues({ ...nextValues });

        // Return to form if invalid
        if (!nextValues.ignoreDomain && !nextValues.validDomain) return;
        if (nextValues.userAlreadyExist) return;
      }

      // Send invite
      return licenseWarningBag.handleAttemptToIncreaseMembers(() =>
        inviteMutation({ email: previousValues.email, role: previousValues.role })
          .then((result) => {
            onSuccess(result);
            setShowSuccessDialog(result.email);
          })
          .catch((error) => console.error(error))
          .finally(() => {
            actions.resetForm();
            close();
          })
      );
    },
  });

  const validateGoogleMailDomain = async (email: string): Promise<boolean> => {
    if (email.endsWith('@gmail.com')) return true;

    const domain = email.slice(email.indexOf('@') + 1);
    if (!domain.length) return false;

    const encodedDomain = encodeURIComponent(domain);

    // Timeout
    let timedOut = false;
    const timeoutPromise = new Promise((resolve) => {
      setTimeout(() => {
        timedOut = true;
        resolve(true);
      }, 3000);
    });

    // Fetch
    const fetchPromise = fetch(`https://dns.google.com/resolve?name=${encodedDomain}&type=MX`);
    await Promise.any<any>([timeoutPromise, fetchPromise]);

    // If timed out
    if (timedOut) return false;

    // Otherwise return results
    const result = await fetchPromise;
    const jsonResult = await result.json();
    return !!jsonResult?.Answer?.some((answer: any) => answer?.data?.indexOf('google.com') > -1);
  };

  const authorized = !!user.team.accessUserCanEditMembersUpTo;

  return (
    <VoxelDialog open={open} onClose={() => setOpen(false)} title="Invite team member">
      <LicenseIncreaseWarningDialog licenseWarningBag={licenseWarningBag} />
      <VoxelDialogContent className="mt-6 flex flex-col gap-3" classMinWidth="min-w-[700px]">
        <VoxelInput
          disabled={!authorized || formikProps.isSubmitting}
          label="Google Account Email Address"
          labelPosition="top"
          placeholder="john.doe@gmail.com"
          value={formikProps.values.email}
          onChange={(event) => {
            formikProps.setValues((prev) => ({
              ...prev,
              email: event.target.value,
              invalidDomain: undefined,
              userAlreadyExist: undefined,
            }));
          }}
          deleteValue={() => formikProps.setFieldValue('email', '')}
          inputWidthClass="w-full"
        />
        <VoxelSelect
          disabled={!authorized || formikProps.isSubmitting}
          label="Role"
          labelPosition="top"
          options={memberRoleList}
          selectedOptionValue={formikProps.values.role}
          setSelectedOption={(option: GenericSelectOption) =>
            formikProps.setFieldValue('role', option.value)
          }
        />
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionCancel />
        <VoxelDialogActionSubmit
          title={
            formikProps.values.validDomain !== undefined && !formikProps.values.validDomain
              ? 'Yes, send invite'
              : 'Send Invite'
          }
          endIcon={<FontAwesomeIcon icon={faPaperPlane} />}
          onClick={formikProps.submitForm}
          disabled={
            !formikProps.values.email ||
            !formikProps.isValid ||
            formikProps.isSubmitting ||
            !!formikProps.values.userAlreadyExist ||
            !authorized
          }
        />
      </VoxelDialogActions>
      {formikProps.values.validDomain !== undefined && !formikProps.values.validDomain && (
        <VoxelAlert
          size="sm"
          iconPosition="center"
          iconSize="lg"
          severity="info"
          roundedClass="rounded-none last:rounded-b-xl"
        >
          Please note that access to Voxel requires a Google account. Are you sure this email
          belongs to a Google email domain?
        </VoxelAlert>
      )}
      {!authorized && (
        <VoxelAlert
          size="sm"
          iconPosition="center"
          iconSize="lg"
          severity="error"
          roundedClass="rounded-none last:rounded-b-xl"
        >
          You do not have permission to invite new team members
        </VoxelAlert>
      )}
      {formikProps.values.userAlreadyExist && (
        <VoxelAlert
          roundedClass="rounded-none last:rounded-b-xl"
          size="sm"
          iconSize="lg"
          iconPosition="top"
          severity="error"
          title="This email has already been invited"
        >
          <ul className="ml-4 list-outside list-disc text-[13px]">
            <li>
              If the user has previously been removed and is on the removed members list, it is
              possible to restore the user as a team member.
            </li>
            <li>
              If the user has been invited and is on the pending invitations list, the user will be
              able to sign in without opening the invitation email.
            </li>
          </ul>
        </VoxelAlert>
      )}
    </VoxelDialog>
  );
};

export default InviteMemberDialog;
